import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { submitAnalysisForm } from '../../lib/analysis/analysisService';

const businessTypes = [
  'E-commerce',
  'SaaS',
  'Service Provider',
  'Local Business',
  'Other'
];

const referralSources = [
  'Google Search',
  'Social Media',
  'Referral',
  'Online Ad',
  'Other'
];

const FreeAnalysisForm = () => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const formData = new FormData(e.currentTarget);

      await submitAnalysisForm({
        firstName: (formData.get('first_name') || '').toString().trim(),
        lastName: (formData.get('last_name') || '').toString().trim(),
        email: (formData.get('email') || '').toString().trim(),
        phone: (formData.get('phone') || '').toString().trim(),
        website: (formData.get('website') || '').toString().trim(),
        businessType: (formData.get('businessType') || '').toString().trim(),
        monthlyRevenue: '',
        marketingBudget: '',
        referralSource: (formData.get('referralSource') || '').toString().trim(),
        mainChallenges: (formData.get('challenges') || '').toString().trim(),
      });

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('analysis_form.error_message'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-emerald-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('analysis_form.success_title')}</h3>
        <p className="text-gray-500 max-w-md mx-auto">{t('analysis_form.success_message')}</p>
      </div>
    );
  }

  const inputClasses = "w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500 transition-all";
  const labelClasses = "block text-sm font-semibold text-gray-700 mb-1.5";

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="first_name" className={labelClasses}>{t('form.first_name')}</label>
          <input
            type="text"
            id="first_name"
            name="first_name"
            placeholder={t('form.first_name_placeholder')}
            required
            className={inputClasses}
          />
        </div>
        <div>
          <label htmlFor="last_name" className={labelClasses}>{t('form.last_name')}</label>
          <input
            type="text"
            id="last_name"
            name="last_name"
            placeholder={t('form.last_name_placeholder')}
            required
            className={inputClasses}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="email" className={labelClasses}>{t('form.email')}</label>
          <input
            type="email"
            id="email"
            name="email"
            placeholder={t('form.email_placeholder')}
            required
            className={inputClasses}
          />
        </div>
        <div>
          <label htmlFor="phone" className={labelClasses}>{t('form.phone')}</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            placeholder={t('form.phone_placeholder')}
            required
            className={inputClasses}
          />
        </div>
      </div>

      <div>
        <label htmlFor="website" className={labelClasses}>{t('analysis_form.website')}</label>
        <input
          type="url"
          id="website"
          name="website"
          placeholder={t('analysis_form.website_placeholder')}
          required
          className={inputClasses}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="businessType" className={labelClasses}>{t('form.business_type')}</label>
          <select id="businessType" name="businessType" required className={inputClasses}>
            <option value="">{t('analysis_form.business_type_placeholder')}</option>
            {businessTypes.map((type) => (
              <option key={type} value={type}>
                {t(`form.business_types.${type.toLowerCase().replace(/[- ]/g, '_')}`)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="referralSource" className={labelClasses}>{t('analysis_form.referral_source')}</label>
          <select id="referralSource" name="referralSource" required className={inputClasses}>
            <option value="">{t('analysis_form.referral_source_placeholder')}</option>
            {referralSources.map((source) => (
              <option key={source} value={source}>{source}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="challenges" className={labelClasses}>{t('analysis_form.challenges')}</label>
        <textarea
          id="challenges"
          name="challenges"
          placeholder={t('analysis_form.challenges_placeholder')}
          rows={4}
          className={inputClasses + ' resize-none'}
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 disabled:opacity-60 disabled:cursor-not-allowed text-lg"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            {t('analysis_form.submitting')}
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            {t('analysis_form.submit')}
          </>
        )}
      </button>
    </form>
  );
};

export default FreeAnalysisForm;
